import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  ModalProps,
  FormProps,
  Select,
  Upload,
  message,
  Switch,
  useSelect,
} from "@pankod/refine-antd";
import { PlusOutlined } from "@ant-design/icons";
import { Label } from "interfaces";
import { file2Base64 } from "@pankod/refine-core";

type Props = {
  formProps: FormProps & { initialValues?: any };
  modalProps: ModalProps;
};

interface PrizeFormValues {
  name: string;
  labelId: string;
  weight: number;
  imageUrl: string;
  notificationText: string;
  isActive: boolean;
  isLimited: boolean;
  onlyTurkcell: boolean;
  stock?: number | null;
  offerId?: number | null;
}

export const PrizeEditModal: React.FC<Props> = ({ formProps, modalProps }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [base64Image, setBase64Image] = useState<string | null>(null);

  // useSelect ile label verilerini çekiyoruz
  const { selectProps: labelSelectProps } = useSelect<Label.LabelItem>({
    resource: "wof/label", // API endpoint'i
    optionLabel: "title", // Gösterilecek alan
    optionValue: "id", // Seçilen değerin alanı
  });

  useEffect(() => {
    if (formProps.initialValues) {
      resetForm();
    }
  }, [formProps.initialValues]);

  const resetForm = () => {
    const initialValues = formProps.initialValues || {};
    formProps.form?.setFieldsValue(initialValues);

    if (initialValues.imageUrl) {
      const existingFile = [
        {
          uid: "1",
          name: "Image",
          status: "done",
          url: initialValues.imageUrl,
        },
      ];
      setFileList(existingFile);
      setBase64Image(initialValues.imageUrl);
    } else {
      setFileList([]);
      setBase64Image(null);
    }
  };

  const onFinish = async (values: PrizeFormValues) => {
    const formattedValues = {
      ...values,
      imageUrl: base64Image || "",
      stock:
        values.isLimited && values.stock != null ? Number(values.stock) : null,
      offerId:
        values.offerId != null && values.offerId !== ("" as any)
          ? Number(values.offerId)
          : null,
    };
    return formProps.onFinish?.(formattedValues);
  };

  const handleUploadChange = async ({ fileList: newFileList }: any) => {
    if (newFileList.length > 1) {
      message.error("Yalnızca 1 fotoğraf yükleyebilirsiniz.");
      return;
    }

    setFileList(newFileList);

    if (newFileList.length > 0) {
      const base64Result = await file2Base64(newFileList[0]);
      setBase64Image(base64Result);
    } else {
      setBase64Image(null);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Yükle</div>
    </div>
  );

  return (
    <Modal
      {...modalProps}
      title="Ödül Düzenle"
      width={400}
      onCancel={(e) => {
        modalProps.onCancel?.(e);
        resetForm();
      }}
    >
      <Form {...formProps} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="İsim"
          name="name"
          rules={[
            {
              required: true,
              message: "İsim boş olamaz.",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Etiket"
          name="labelId"
          rules={[
            {
              required: true,
              message: "Etiket seçimi zorunludur.",
            },
          ]}
        >
          <Select
            {...labelSelectProps}
            placeholder="Etiket seçin"
            allowClear={false}
          />
        </Form.Item>

        <Form.Item
          label="Ağırlık"
          name="weight"
          rules={[
            {
              required: true,
              message: "Ağırlık boş olamaz.",
            },
          ]}
        >
          <Input type="number" min={0} />
        </Form.Item>

        <Form.Item
          label="Fotoğraf"
          name="imageUrl"
          tooltip="Opsiyonel. Boş bırakılırsa ödül görselsiz kaydedilir."
        >
          <Upload.Dragger
            name="files"
            listType="picture-card"
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={() => false}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload.Dragger>
        </Form.Item>

        <Form.Item
          label="Bildirim Metni"
          name="notificationText"
          rules={[
            {
              required: true,
              message: "Bildirim metni boş olamaz.",
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          label="Partner Offer ID"
          name="offerId"
          tooltip="Promo ödüller için partner offer ID'si. Boş bırakılırsa non-promo ödül olarak işlenir."
        >
          <Input type="number" min={0} placeholder="Örn: 10120" />
        </Form.Item>

        <Form.Item
          label="Stok Sınırlı Mı?"
          name="isLimited"
          valuePropName="checked"
          tooltip="Açıkken Stok alanı zorunlu olur ve her dağıtımda 1 azaltılır."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.isLimited !== cur.isLimited}
        >
          {({ getFieldValue }) =>
            getFieldValue("isLimited") ? (
              <Form.Item
                label="Stok"
                name="stock"
                rules={[
                  { required: true, message: "Stok zorunludur." },
                  {
                    validator: (_, v) =>
                      v == null || Number(v) >= 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("Stok 0 veya pozitif olmalı.")),
                  },
                ]}
              >
                <Input type="number" min={0} />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          label="Sadece Turkcell Müşterileri Mi?"
          name="onlyTurkcell"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Aktif Mi?"
          name="isActive"
          valuePropName="checked"
          rules={[
            {
              required: true,
              message: "Aktiflik durumu seçmek zorunludur.",
            },
          ]}
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};
